import React from 'react'
import type { Appointment, OpeningWindow, TimeOff } from '../../api/client'
import { useApp } from '../../context/AppContext'
import { formatLongDay } from '../../calendar/dates'
import { monthGrid, type MonthBooking, type MonthDay } from '../../calendar/monthGrid'
import { WEEKDAYS, formatPrice } from '../../catalog/format'

interface MonthViewProps {
  date: string
  today: string
  appointments: Appointment[]
  windows: OpeningWindow[]
  timeOff: TimeOff[]
}

const maxDots = 4
const maxNames = 2

function dot(b: MonthBooking) {
  return (
    <span
      key={b.id}
      className={`month-day__dot${b.done ? ' month-day__dot--done' : ''}`}
      style={{ '--cat': b.colour } as React.CSSProperties}
    />
  )
}

function dayLabel(day: MonthDay): string {
  const count = `${day.count} ${day.count === 1 ? 'appointment' : 'appointments'}`
  return `${formatLongDay(day.date)}, ${count}${day.timeOff ? ', time off' : day.closed ? ', closed' : ''}`
}

// MonthView: the month at a glance, each day a button that opens it. On a phone
// a day has its count and up to four dots in its treatments' colours; from
// 768px, the first two clients' names and how many more. Days she's closed or
// on time off are shaded, and say so unless a booking sits on them anyway.
export default function MonthView({ date, today, appointments, windows, timeOff }: MonthViewProps) {
  const { dispatch } = useApp()
  const grid = monthGrid(date, windows, timeOff, appointments, today)

  const openDay = (day: string) => {
    dispatch({ type: 'SET_CALENDAR_DATE', payload: day })
    dispatch({ type: 'SET_CALENDAR_VIEW', payload: 'day' })
  }

  return (
    <div className="month-view">
      <p className="calendar-summary">
        {grid.count} {grid.count === 1 ? 'appointment' : 'appointments'} · {formatPrice(grid.bookedPence)} booked
      </p>
      <div className="month-weekdays" aria-hidden="true">
        {WEEKDAYS.map((weekday) => (
          <span key={weekday}>{weekday.slice(0, 3)}</span>
        ))}
      </div>
      <div className="month-grid">
        {grid.days.map((day) => (
          <button
            key={day.date}
            type="button"
            className={[
              'month-day',
              !day.inMonth && 'month-day--outside',
              (day.closed || day.timeOff) && 'month-day--off',
              day.isToday && 'month-day--today',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={dayLabel(day)}
            aria-current={day.isToday ? 'date' : undefined}
            onClick={() => openDay(day.date)}
          >
            <span className="month-day__date" aria-hidden="true">{day.dayOfMonth}</span>
            <span className="month-day__phone" aria-hidden="true">
              {day.count > 0 ? (
                <>
                  <span className="month-day__count">{day.count}</span>
                  <span className="month-day__dots">{day.bookings.slice(0, maxDots).map(dot)}</span>
                </>
              ) : (
                day.timeOff && <span className="month-day__note">Off</span>
              )}
            </span>
            <span className="month-day__wide" aria-hidden="true">
              {day.count > 0 ? (
                <>
                  {day.bookings.slice(0, maxNames).map((b) => (
                    <span key={b.id} className="month-day__booking">
                      {dot(b)}
                      <span className="month-day__name">{b.name}</span>
                    </span>
                  ))}
                  {day.count > maxNames && <span className="month-day__note">+{day.count - maxNames} more</span>}
                </>
              ) : (
                (day.timeOff || day.closed) && <span className="month-day__note">{day.timeOff ? 'Time off' : 'Closed'}</span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
